/**
 * Resolves the social-card image for a blog post and records which source in
 * the precedence chain won, so precedence bugs are diagnosable at a glance.
 *
 * Precedence: branded auto-generated card -> post featuredImage -> site default.
 */

export const DEFAULT_OG_IMAGE =
  "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg";

export type OgImageSource = "auto-card" | "featured-image" | "site-default";

export interface ResolvedOgImage {
  url: string;
  source: OgImageSource;
  /** Candidates considered, in precedence order, for debug output. */
  candidates: { source: OgImageSource; url: string | undefined }[];
}

export function resolveOgImage(options: {
  slug?: string;
  autoCard?: string;
  featuredImage?: string;
}): ResolvedOgImage {
  const candidates: ResolvedOgImage["candidates"] = [
    { source: "auto-card", url: options.autoCard },
    { source: "featured-image", url: options.featuredImage },
    { source: "site-default", url: DEFAULT_OG_IMAGE },
  ];

  const winner = candidates.find((c) => Boolean(c.url))!;
  const resolved: ResolvedOgImage = {
    url: winner.url!,
    source: winner.source,
    candidates,
  };

  logOgImageChoice(options.slug ?? "(unknown post)", resolved);
  return resolved;
}

/** Dev-only structured log; silent in production builds. */
export function logOgImageChoice(slug: string, resolved: ResolvedOgImage): void {
  if (!import.meta.env.DEV) return;
  // eslint-disable-next-line no-console
  console.info(
    `[og-image] ${slug} -> ${resolved.source}: ${resolved.url}`,
    resolved.candidates
      .map((c) => `${c.source}=${c.url ?? "(none)"}`)
      .join(" | "),
  );
}
